import React, { useState, useRef, useCallback } from 'react';
import { observer } from 'mobx-react';
import {
  TableContainer,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Skeleton,
} from '@chakra-ui/react';
import { TableRowMemo, getProp } from './TableRow';

export interface TableContentProps {
  nodes: any[];
  columns: any[];
  loading: boolean;
  tableStore?: any;
  subRowsAccessor?: string;
  showEmptyPlaceholder: boolean;
  emptyTablePlaceholder: any; // react element
  staticLastRow?: any;
  maxRows?: number;
  RowActionButtons?: React.FunctionComponent<any>;
  shouldRowBeGreyedOut?: (node: any) => boolean;
  getHoveredComponent?: (node: any) => JSX.Element;
  loadingRowCount?: number;
  conn?: any;
  dataTestId?: string;
  // TODO: deal with 'conn'
}

export const TableContent = observer(
  ({
    nodes = [],
    columns,
    loading,
    tableStore,
    subRowsAccessor,
    showEmptyPlaceholder,
    emptyTablePlaceholder,
    staticLastRow,
    RowActionButtons,
    maxRows = -1,
    loadingRowCount,
    conn,
    getHoveredComponent,
    shouldRowBeGreyedOut,
    dataTestId,
  }: TableContentProps) => {
    const tableBodyRef = useRef(null);
    const [hoveredRowId, setHoveredRowId] = useState(null);
    const loadingRows = [];

    const handleMouseOver = useCallback((rowId: number) => {
      setHoveredRowId(rowId);
    }, []);
    const handleMouseLeave = useCallback(() => setHoveredRowId(null), []);

    if (maxRows !== -1 && nodes.length) {
      // eslint-disable-next-line no-param-reassign
      nodes = nodes.slice(0, maxRows);
    }
    for (let b = 0; b < loadingRowCount; b += 1) {
      loadingRows.push(
        <Tr height='60px' key={b}>
          {columns.map((col, i) => {
            const shouldShowSkeleton = !!col.Header && i === 0;

            if (shouldShowSkeleton)
              return (
                <Td
                  whiteSpace='nowrap'
                  pt='5px'
                  pb='5px'
                  borderColor='gray.200'
                  key={i}
                >
                  <Skeleton height='20px' />
                </Td>
              );
            return <Td borderColor='gray.200' key={i} />;
          })}
        </Tr>,
      );
    }
    // TODO: put <TBody> in a separate component so we don't re-render every row when hoveredRowId changes.
    return (
      <TableContainer borderWidth='1px' borderColor='gray.200' my={8}>
        <Table fontSize='sm' data-testid={dataTestId}>
          <Thead bg='gray.50'>
            <Tr>
              {columns.map((column, index) => (
                <Th
                  color='black'
                  whiteSpace='nowrap'
                  scope='col'
                  key={index}
                  borderColor='gray.200'
                  isNumeric={column.isNumeric}
                >
                  {column.Header && column.Header}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody ref={tableBodyRef}>
            {loading && <>{loadingRows}</>}
            {!loading &&
              nodes.map((node, index) => {
                const rowId = index; // we use index as id for now
                const isHovered = hoveredRowId === rowId;
                let subRows = getProp(node, subRowsAccessor);
                if (!subRows || subRows.length <= 1) {
                  subRows = [];
                }
                return (
                  <TableRowMemo
                    columns={columns}
                    loading={loading}
                    subRows={subRows}
                    index={index}
                    nodes={nodes}
                    getHoveredComponent={getHoveredComponent}
                    node={node}
                    conn={conn}
                    key={rowId}
                    shouldRowBeGreyedOut={shouldRowBeGreyedOut}
                    tableStore={tableStore}
                    RowActionButtons={RowActionButtons}
                    handleMouseOver={handleMouseOver}
                    handleMouseLeave={handleMouseLeave}
                    isHovered={isHovered}
                  />
                );
              })}
            {staticLastRow}
            {showEmptyPlaceholder && (
              <Tr>
                <Td colSpan={columns.length}>{emptyTablePlaceholder}</Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    );
  },
);

// TableContentInner.defaultProps = {
//  maxRows: -1,
// };

/*
*Nested Table Row*

<Tr>
  <Td colspan={1}>Quotes</Td>
  <Td colspan={columns.length}>
    <Table my='8' borderWidth='1px' fontSize='sm'>
      <Thead bg={mode('gray.50', 'gray.800')}>
        <Tr>
          {columns.map((column, index2) => (
            <Th whiteSpace='nowrap' scope='col' key={index2}>
              {column.Header && column.Header}
            </Th>
          ))}
          <Th />
        </Tr>
      </Thead>
      <Tbody>
        <Tr key={index}>
          {columns.map((column, ix) => {
            const cell = getProp(row, column.accessor);
            const element = column.Cell?.(cell) ?? cell;
            return (
              <Td
                whiteSpace='nowrap'
                key={ix}
                textAlign={ix === columns.length - 1 && 'right'}
              >
                {element}
              </Td>
            );
          })}
          <Td />
        </Tr>
      </Tbody>
    </Table>
  </Td>
</Tr>;
*/
